import { forwardRef, useEffect, useRef, useState } from 'react';
import { Graph } from './Graph.component';
import { IGraphProps, GraphRef } from './types';
import { useStaggeredMount } from './hooks/use-staggered-mount.hook';

export interface ILazyGraphProps extends IGraphProps {
  /**
   * When true, the chart waits for IntersectionObserver visibility (200px rootMargin)
   * before entering the stagger queue. When false (default), it enters the queue on mount.
   * Use `defer` for below-the-fold charts.
   */
  defer?: boolean;
}

const getPlaceholderHeight = (height: IGraphProps['height']): string | number => {
  if (typeof height === 'number' && !isNaN(height)) return height;

  if (typeof height === 'string') return height;

  return '100%';
};

interface IGraphPlaceholderProps {
  height: IGraphProps['height'];
  containerRef?: React.Ref<HTMLDivElement>;
}

const GraphPlaceholder: React.FC<IGraphPlaceholderProps> = ({ height, containerRef }) => (
  <div ref={containerRef} style={{ height: getPlaceholderHeight(height), width: '100%' }} aria-hidden="true" />
);

/**
 * Second stage of `LazyGraph`: once the chart is confirmed visible, enter the stagger
 * queue. Renders the same placeholder until `useStaggeredMount` releases it, then renders
 * the real Graph. Separated from LazyGraph so `useStaggeredMount` is only ever mounted
 * post-visibility — the hook always runs in its "enabled on mount" mode, avoiding the
 * false → true transition hazard that a shared `enabled` flag would introduce.
 */
const StaggeredGraph = forwardRef<GraphRef, IGraphProps>((props, ref) => {
  const { isReady } = useStaggeredMount();

  if (!isReady) return <GraphPlaceholder height={props.height} />;

  return <Graph {...props} ref={ref} />;
});

StaggeredGraph.displayName = 'StaggeredGraph';

export const LazyGraph = forwardRef<GraphRef, ILazyGraphProps>(({ defer = false, ...props }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(!defer);

  useEffect(() => {
    if (!defer || isVisible) return;

    const el = containerRef.current;

    if (!el || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);

      return;
    }

    const io = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setIsVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px' },
    );

    io.observe(el);

    return () => io.disconnect();
  }, [defer, isVisible]);

  // Keep the placeholder (and its ref'd node) in the DOM so the IntersectionObserver has
  // something to observe. Only once visible do we hand off to `StaggeredGraph`, which
  // owns the independent stagger-queue gate.
  if (!isVisible) return <GraphPlaceholder height={props.height} containerRef={containerRef} />;

  return <StaggeredGraph {...props} ref={ref} />;
});

LazyGraph.displayName = 'LazyGraph';
